import { useMemo, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedDevice, setSelectedDevice } from '../../redux/app/platformSlice';
import { selectMarket } from '../../redux/app/configSlice';
import {
  getDevicesAsync,
  selectDevices,
  selectDevicesLoading,
} from '../../redux/devices/getDevicesSlice';
import { filterDevicesByMarket } from '../../services/flowApiService';

export function SelectDeviceForAppiumAutomation() {
  const selectedDevice = useSelector(selectSelectedDevice);
  const devices = useSelector(selectDevices);
  const loading = useSelector(selectDevicesLoading);
  const market = useSelector(selectMarket);
  const dispatch = useDispatch();

  const activeDevices = useMemo(
    () => filterDevicesByMarket(devices, market?.id).filter((d) => !d.deleted_at),
    [devices, market],
  );

  // A device belongs to a single market — drop the persisted selection when the
  // market changes. Uses the selected device's own market_id so we don't need the
  // full device list loaded (it's now fetched lazily, on dropdown open).
  useEffect(() => {
    if (selectedDevice && market?.id && Number(selectedDevice.market_id) !== Number(market.id)) {
      dispatch(setSelectedDevice(null));
    }
  }, [market?.id, selectedDevice, dispatch]);

  const handleDeviceChange = (_, device) => {
    dispatch(setSelectedDevice(device ?? null));
  };

  // Fetch the device list only when the user opens the dropdown (and only once —
  // the list is cached in Redux for the rest of the session).
  const handleOpen = () => {
    if (devices.length === 0 && !loading) {
      dispatch(getDevicesAsync());
    }
  };

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={activeDevices}
      value={selectedDevice ?? null}
      onChange={handleDeviceChange}
      onOpen={handleOpen}
      loading={loading}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, value) => option.device_id_1 === value.device_id_1}
      noOptionsText={loading ? 'Loading devices…' : 'No Active Device'}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1,
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: 'text.secondary',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: 'primary.main',
        },
      }}
      renderInput={(params) => <TextField {...params} label="Device (Required)" />}
    />
  );
}
